/* eslint-disable max-classes-per-file */

import { Component } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Alert, alertTypes } from '../Alert';
import { Button, buttonColors } from '../Button';
import type { ButtonColor } from '../Button';
import { ButtonToolbar } from '../ButtonToolbar';
import { Modal, modalSizes, ModalProps } from '../Modal';
import type { ModalSize } from '../Modal';

import { showModal } from '../showModal';

export const StyledContent = styled.div(({ theme: { colors } }) => ({
  color: colors.NEUTRAL_DEFAULT,
}));

const StyledErrorWrapper = styled.div(({ theme: { indents } }) => ({
  marginTop: indents.INDENT_4,
}));

export type ConfirmErrorParams = {
  title?: ReactNode;
  message?: ReactNode;
};

export class ConfirmError extends Error {
  title: ReactNode;

  errorMessage: ReactNode;

  constructor({ title, message }: ConfirmErrorParams) {
    super('Confirm error');

    this.title = title;
    this.errorMessage = message;
  }
}

export type ShowConfirmParams = {
  /**
   * Размер модального окна
   */
  modalSize?: ModalSize;
  /**
   * Заголовок
   */
  title?: string;
  /**
   * Текст
   */
  content?: ReactNode;
  /**
   * Элемент для монтирования модального окна
   */
  container?: HTMLElement | null;
  /**
   * Асинхронная функция подтверждения, может закончиться с исключением ConfirmError({ title, message })
   */
  approve?: () => void | Promise<void>;
  /**
   * Текст кнопки отмены
   */
  cancelButtonText: string;
  /**
   * Скрыть кнопку отмены
   */
  hideCancelButton?: boolean;
  /**
   * Текст кнопки подтверждения
   */
  approveButtonText: string;
  /**
   * Цвет кнопки подтверждения
   */
  approveButtonColor?: ButtonColor;
} & Pick<ModalProps, 'isHideOnBackdropClick'>;

export type ConfirmProps = ShowConfirmParams & {
  resolveModal: (result: boolean) => void;
};

export type ConfirmState = {
  approving: boolean;

  error?: {
    title?: ReactNode;
    message?: ReactNode;
  };
};

export class Confirm extends Component<ConfirmProps, ConfirmState> {
  constructor(props: ConfirmProps) {
    super(props);

    this.state = {
      approving: false,
      error: undefined,
    };
  }

  handleCancel = (): void => {
    const { approving } = this.state;

    if (approving) {
      return;
    }

    const { resolveModal } = this.props;

    resolveModal(false);
  };

  handleApprove = async (): Promise<void> => {
    const { approving } = this.state;

    if (approving) {
      return;
    }

    const { approve } = this.props;

    // eslint-disable-next-line @typescript-eslint/await-thenable
    await this.setState({
      approving: true,
      error: undefined,
    });

    if (approve) {
      let error: ConfirmError | undefined;

      try {
        await approve();
      } catch (e) {
        if (e instanceof ConfirmError) {
          error = e;
        } else {
          throw e;
        }
      }

      if (error) {
        // eslint-disable-next-line @typescript-eslint/await-thenable
        await this.setState({
          approving: false,

          error: {
            title: error.title,
            message: error.errorMessage,
          },
        });

        return;
      }
    }

    const { resolveModal } = this.props;

    resolveModal(true);
  };

  render(): ReactNode {
    const {
      modalSize,
      title,
      content,
      container,
      cancelButtonText,
      approveButtonText,
      approveButtonColor,
      isHideOnBackdropClick,
      hideCancelButton,
    } = this.props;

    const { approving, error } = this.state;

    return (
      <Modal
        show
        onHide={this.handleCancel}
        size={modalSize}
        isHideOnBackdropClick={isHideOnBackdropClick}
        container={container}
      >
        <>
          {title && <Modal.Title>{title}</Modal.Title>}
          <Modal.Body>
            {content && <StyledContent>{content}</StyledContent>}

            {error && (
              <StyledErrorWrapper>
                <Alert type={alertTypes.DANGER} title={error.title} noMargins>
                  {error.message}
                </Alert>
              </StyledErrorWrapper>
            )}
          </Modal.Body>

          <Modal.Footer>
            <ButtonToolbar align="right">
              <Button
                componentProps={{
                  type: 'button',
                }}
                disabled={approving}
                color={approveButtonColor}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onClick={this.handleApprove}
              >
                {approveButtonText}
              </Button>

              {!hideCancelButton && (
                <Button
                  componentProps={{
                    type: 'button',
                  }}
                  disabled={approving}
                  color={buttonColors.GHOST}
                  onClick={this.handleCancel}
                >
                  {cancelButtonText}
                </Button>
              )}
            </ButtonToolbar>
          </Modal.Footer>
        </>
      </Modal>
    );
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Confirm.defaultProps = {
  container: null,
  modalSize: modalSizes.SMALL,
  title: null,
  content: null,
  approve: null,
  approveButtonColor: buttonColors.PRIMARY,
  isHideOnBackdropClick: false,
  hideCancelButton: false,
};

export const showConfirm = ({
  title,
  content,
  container,
  approve,
  cancelButtonText,
  approveButtonText,
  approveButtonColor,
  modalSize,
  isHideOnBackdropClick,
  hideCancelButton,
}: ShowConfirmParams): Promise<boolean> => showModal<boolean>(({ resolveModal }) => (
  <Confirm
    modalSize={modalSize}
    title={title}
    content={content}
    container={container}
    approve={approve}
    cancelButtonText={cancelButtonText}
    approveButtonText={approveButtonText}
    approveButtonColor={approveButtonColor}
    resolveModal={resolveModal}
    isHideOnBackdropClick={isHideOnBackdropClick}
    hideCancelButton={hideCancelButton}
  />
));
